import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { PriceDiffDisplay } from "components/common/plan/MixPriceDiff";
import { useTenantTranslation } from "hooks/formatters";
import { ActiveHeatOverride } from "src/store/api/generatedApi";
import { ChargeDeltaList } from "../delta/ChargeDeltaList";
import { useFormatOverrideRequested } from "./useFormatOverrideRequested";
import { useOverrideCostDelta } from "./useOverrideCostSummaries";
import {
  formatOverrideReason,
  OverrideComment,
  OverrideMixComparison,
} from "./OverrideDetailSections";

export const OverrideDetailsDialog = ({
  override,
  claimedHeatIds,
  onClose,
  onCancel,
  isCancelling,
  cancelFailed,
}: {
  override: ActiveHeatOverride;
  claimedHeatIds: number[];
  onClose: () => void;
  onCancel: (() => Promise<void>) | null;
  isCancelling: boolean;
  cancelFailed: boolean;
}) => {
  const { t } = useTenantTranslation();
  const formatOverrideRequested = useFormatOverrideRequested();
  const { deltaPerTonne, isPending } = useOverrideCostDelta({
    overrideMixId: override.override_mix_id,
    replacedMixId: override.replaced_mix.id,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="override-details-title"
    >
      <DialogTitle id="override-details-title">
        {t("overrideDetails")}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2 }}>
          {override.charge_deltas !== null ? (
            <ChargeDeltaList chargeDeltas={override.charge_deltas} />
          ) : (
            <>
              <OverrideMixComparison
                replacedMix={override.replaced_mix}
                overrideMix={override.override_mix}
                recipeName={override.recipe_name}
              />
              <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
                <Typography variant="body2">
                  {t("overrideCostChange")}
                  {":"}
                </Typography>
                {deltaPerTonne !== null ? (
                  <PriceDiffDisplay
                    priceDiff={deltaPerTonne}
                    unitType="specific_cost"
                  />
                ) : isPending ? (
                  <Skeleton width={80} />
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("overrideCostUnavailable")}
                  </Typography>
                )}
              </Stack>
            </>
          )}
          <Typography variant="body2">
            {formatOverrideRequested(override)}
          </Typography>
          <Stack sx={{ gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("heats")}
            </Typography>
            <Typography variant="body2Mono">
              {claimedHeatIds.join(", ")}
            </Typography>
          </Stack>
          {override.reason !== null && (
            <Stack sx={{ gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("overrideReason")}
              </Typography>
              <Typography variant="body2">
                {formatOverrideReason(override)}
              </Typography>
            </Stack>
          )}
          <OverrideComment comment={override.comment} />
          {cancelFailed && (
            <Alert severity="error">{t("cancelOverrideError")}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {onCancel !== null && (
          <Button
            size="small"
            color="error"
            disabled={isCancelling}
            onClick={() => {
              void onCancel();
            }}
          >
            {t("cancelOverride")}
          </Button>
        )}
        <Button onClick={onClose}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};
